import { useEffect, type ReactElement } from 'react'
import {
	type ErrorResponse,
	isRouteErrorResponse,
	useParams,
	useRouteError,
} from 'react-router'
import { FoutScherm } from '#app/components/heute/fout-scherm.tsx'
import { HydrationMarker } from '#app/components/hydration-marker.tsx'
import { useT } from '#app/utils/i18n.tsx'

type StatusHandler = (info: {
	error: ErrorResponse
	params: Record<string, string | undefined>
}) => ReactElement | null

export function GeneralErrorBoundary({
	defaultStatusHandler,
	statusHandlers,
	unexpectedErrorHandler,
}: {
	defaultStatusHandler?: StatusHandler
	statusHandlers?: Record<number, StatusHandler>
	unexpectedErrorHandler?: (error: unknown) => ReactElement | null
}) {
	const error = useRouteError()
	const params = useParams()
	// the I18nProvider mounts in entry.server/entry.client ABOVE the router,
	// so even the root boundary can translate (see utils/i18n.tsx)
	const t = useT()
	const isResponse = isRouteErrorResponse(error)

	if (typeof document !== 'undefined') {
		console.error(error)
	}

	useEffect(() => {
		if (isResponse) return

		// lazy: Sentry must never ride the critical path of every route
		// (perf budget gate — the static import cost ~30KB gz on /vandaag)
		void import('@sentry/react-router').then(({ captureException }) =>
			captureException(error),
		)
	}, [error, isResponse])

	// human copy by default (ontwerp v4: FoutScherm); the raw error stays in
	// the console + Sentry. Alleen een 5xx lag aan ons en gaat misschien zo
	// vanzelf over; bij een andere 4xx helpt opnieuw proberen niet
	const renderStatus: StatusHandler =
		defaultStatusHandler ??
		(({ error }) =>
			error.status === 404 ? (
				<FoutScherm
					titel={t('boundary.notfound_title')}
					tekst={t('boundary.notfound_body')}
				/>
			) : (
				<FoutScherm
					titel={t('boundary.fout_title')}
					tekst={t(
						error.status >= 500 ? 'boundary.fout_body' : 'boundary.client_body',
					)}
					code={error.status}
				/>
			))
	const renderUnexpected =
		unexpectedErrorHandler ??
		(() => (
			<FoutScherm
				titel={t('boundary.fout_title')}
				tekst={t('boundary.unexpected_body')}
			/>
		))

	return (
		<>
			{isResponse
				? (statusHandlers?.[error.status] ?? renderStatus)({
						error,
						params,
					})
				: renderUnexpected(error)}
			<HydrationMarker />
		</>
	)
}
